<template>
  <settings-form :breadcrumbs="useSettingsBreadcrumbs()">
    <website-qr-dialog
      v-if="qrDialogVisible"
      :visible="qrDialogVisible"
      :hosted-form-url="hostedFormUrl"
      @close="qrDialogVisible = false"
    />
    <input-dialog
      v-model="widgetSubdomain"
      :label-title="$t('label.booking_url')"
      :visible="urlEditorVisible"
      :message="$t('message.enter_booking_form_url')"
      confirm-title="save"
      max-width="400"
      :textfield-suffix="`.${WIDGET_DOMAIN}`"
      rules="alpha_num|min:3|max:50|unique_widget_url"
      no-auto-close
      debounce-validation="300"
      @confirm="saveURLEditor"
      @cancel="urlEditorVisible = false"
    />
    <template #actions />
    <template #content>
      <validation-observer ref="observerRef">
        <settings-group
          :title="$t('settings.online-integration.widget_title')"
          :subtitle="$t('settings.online-integration.widget_subtitle')"
        >
          <v-expansion-panels
            mandatory
            accordion
            flat
          >
            <v-expansion-panel>
              <v-expansion-panel-header class="text-h6 font-weight-medium expansion-header-first">
                {{ $t('label.website') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                {{ $t('label.widget_website_code_instruction') }}
                <div class="code-preview">
                  <highlightjs
                    class="code-block mt-2"
                    language="xml"
                    :autodetect="false"
                    :code="widgetScript"
                  />
                  <v-btn
                    dark
                    icon
                    class="code-copy-btn"
                    @click="copyCode(widgetScript)"
                  >
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <p class="pt-2">
                  {{ $t('label.widget_documentation_description') }} <a
                    href="https://docs.guestplan.com/#/widget"
                    target="_blank"
                  >{{ $t('label.documentation') }}</a>.
                </p>
              </v-expansion-panel-content>
              <v-divider />
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header class="text-h6 font-weight-medium">
                {{ $t('label.wordpress_plugin') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <span class="text-overline d-block">
                  {{ $t('label.install_wordpress_plugin') }}
                </span>
                <i18n
                  path="label.wordpress-step-install"
                  tag="p"
                >
                  <a
                    slot="link"
                    href="https://wordpress.org/plugins/guestplan-booking-widget/"
                    target="_blank"
                  >{{ $t('button.download_plugin') }}</a>
                </i18n>
                <v-img
                  src="/assets/widget/wp_step_1.png"
                  contain
                />

                <span class="text-overline d-block pt-4">
                  {{ $t('label.configure_widget') }}
                </span>
                <p>
                  {{ $t('label.widget_api_key_configuration') }}
                </p>
                <p class="pb-0 mb-2">
                  {{ $t('label.your_api_key') }}
                </p>
                <div class="code-preview">
                  <highlightjs
                    class="code-block mb-2"
                    language="xml"
                    :autodetect="false"
                    :code="accessKey"
                  />
                  <v-btn
                    dark
                    icon
                    class="code-copy-btn"
                    @click="copyCode(accessKey ?? '')"
                  >
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <v-img
                  contain
                  src="/assets/widget/wp_step_2.png"
                  class="pt-2"
                />
                <p />
              </v-expansion-panel-content>
              <v-divider />
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header class="text-h6 font-weight-medium">
                {{ $t('label.wix_website_builder') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                {{ $t('label.wix_code_instruction') }} <a
                  href="https://docs.guestplan.com/#/widget?id=wix-website"
                  target="_blank"
                >
                  {{ $t('label.wix_integration_guide') }}</a>:
                <div class="code-preview">
                  <highlightjs
                    class="code-block mt-2"
                    language="xml"
                    :autodetect="false"
                    :code="widgetScript"
                  />
                  <v-btn
                    dark
                    icon
                    class="code-copy-btn"
                    @click="copyCode(widgetScript)"
                  >
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </settings-group>
        <settings-group
          :title="$t('settings.online-integration.widget_link_title')"
          :subtitle="$t('settings.online-integration.widget_link_subtitle')"
          gutter
        >
          <v-col
            cols="12"
            class="pa-0"
          >
            <settings-button-block
              :title="$t('title.booking_website')"
              :description="$t('label.description_widget_qr')"
            >
              <v-btn
                color="primary"
                outlined
                :block="$vuetify.breakpoint.xsOnly"
                @click="qrDialogVisible = true"
              >
                <v-icon class="mr-2">
                  mdi-qrcode
                </v-icon>
                {{ $t('button.create_qr_code') }}
              </v-btn>
            </settings-button-block>
            <p class="pa-3 mb-0">
              {{ $t('label.description_widget_hosted_form') }}
            </p>
            <v-sheet
              :color="$vuetify.theme.dark ? 'secondary lighten-2' : 'grey lighten-3'"
              class="mx-3 mt-0 pt-1 text-center"
            >
              <a
                :href="hostedFormUrl"
                target="_blank"
              >{{ hostedFormUrl }}</a>
              <v-icon
                small
                class="pl-1 pb-1"
              >
                mdi-open-in-new
              </v-icon>
              <div>
                <v-btn
                  class="mt-1 mb-3 blue white--text"
                  x-small
                  outlined
                  @click="openURLEditor"
                >
                  <v-icon
                    x-small
                    class="mr-1"
                  >
                    mdi-pencil
                  </v-icon>
                  {{ $t('button.change_url_address') }}
                </v-btn>
              </div>
            </v-sheet>
          </v-col>
        </settings-group>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import { cloneModel } from '@/model/model-utils';
import { performAction } from '@/services/vue-utils';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import SettingsGroup from '@/components/layouts/SettingsGroup.vue';
import WebsiteQrDialog from '@/components/views/settings/WebsiteQrDialog.vue';
import SettingsButtonBlock from '@/components/forms/SettingsButtonBlock.vue';
import InputDialog from '@/components/dialogs/InputDialog.vue';
import grpcClient from '@/grpc-api/grpc-client';
import Campaign from '@/grpc-api/model/campaign';
import { ValidationObserver, extend } from 'vee-validate';
import { hostedWidgetURL, WIDGET_DOMAIN } from '@/services/configuration';
import store from '@/store/store';
import i18nn from '@/plugins/i18n';
import useStoreWatch from '@/composables/store-watch';
import { ApiErrorCode } from '@/api/api-error';

import { useToast } from '../helpers/vue2-helpers';

const toast = useToast();

const qrDialogVisible = ref(false);

const widgetSubdomain = ref(null as string | null);

const widgetDocumentationURL = computed(() => store.state.settings.account.widgetDocumentationURL || '');

const accessKey = computed(() => store.state.settings.account.defaultAccessKey);

// Campaign
const campaign = ref(null as Campaign | null);

const loadCampaign = async (): Promise<boolean> => {
  const ok = await performAction(
    null,
    i18nn.tc('error.load_data'),
    async () => {
      campaign.value = await grpcClient.getDefaultCampaign();
      widgetSubdomain.value = campaign.value.subdomain ?? null;
    },
  );
  return ok;
};

const updateCampaign = async (c: Campaign): Promise<boolean> => {
  const ok = await performAction(
    null,
    i18nn.tc('error.save_data'),
    async () => {
      campaign.value = await grpcClient.updateDefaultCampaign(c);
    },
  );
  return ok;
};

// widget dialog
const urlEditorVisible = ref(false);

const hostedFormUrl = computed(() => {
  const subdomain = campaign.value?.subdomain;
  return subdomain ? hostedWidgetURL({ subdomain }) : store.getters.hostedWidgetURL ?? 'N/A';
});

const openURLEditor = async () => {
  const ok = await loadCampaign();
  if (ok) urlEditorVisible.value = true;
};

const saveURLEditor = async () => {
  if (!campaign.value) { urlEditorVisible.value = false; return; }

  const nc = cloneModel(campaign.value);
  nc.subdomain = widgetSubdomain.value ?? undefined;

  const ok = await updateCampaign(nc);
  if (ok) urlEditorVisible.value = false;
};

// store watch, load data
const reset = async () => {
  campaign.value = null;
  widgetSubdomain.value = null;
  if (!store.getters.isLoaded) return;

  await loadCampaign();
};
useStoreWatch(reset, true);

onMounted(() => {
  const testCampaign = async (c: Campaign): Promise<string|boolean> => {
    try {
      await grpcClient.testDefaultCampaign(c);
    } catch (e: any) {
      if (e.code === ApiErrorCode.already_exists || e.code === ApiErrorCode.subdomain_already_exists) {
        return i18nn.tc('error.url_already_taken');
      }
      if (e.code === ApiErrorCode.invalid_argument || e.code === ApiErrorCode.subdomain_invalid) {
        return i18nn.tc('error.url_not_allowed');
      }
      return true;
    }
    return true;
  };

  extend('unique_widget_url', {
    validate: async (value, _) => {
      if (campaign.value === null || value === undefined) return true;
      const nc = cloneModel(campaign.value);
      nc.subdomain = value;
      return testCampaign(nc);
    },
    lazy: true,
  });
});

const widgetScript = computed(() => ` <script>
    (function(g,s,t,p,l,n){
      g["_gstpln"]={};
      (l=s.createElement(t)),(n=s.getElementsByTagName(t)[0]);
      l.async=1;l.src=p;n.parentNode.insertBefore(l,n);
    })(window,document,"script","https://cdn.guestplan.com/widget.js");
    _gstpln.accessKey = "${accessKey.value}";\n`
  // eslint-disable-next-line no-useless-escape
  + '<\/script>');

const copyCode = (code: string) => {
  navigator.clipboard.writeText(code);
  toast.success(i18nn.t('message.code_copied'));
};

</script>

<style lang="scss">
  .v-application .code-block code.hljs {
    color: #c9d1d9;
    background: #0d1117;
  }

  .code-preview {
    border-radius: 8px;
    position: relative;
  }
  .code-preview .code-copy-btn {
    position: absolute;
    top: 0;
    right: 0;
  }
  .expansion-header-first {
    min-height: unset !important;
    margin-top: 0px;
    padding-top: 0px;
  }
</style>
